import { Injectable } from '@angular/core';
import { PublicContentSection } from '../../../../../../api/src/content-section/content-section.entity';

// Type definitions for transformer input
// Matches LocaleDto from API (recursive structure for nested translations)
interface SectionLocales {
	[key: string]: string | SectionLocales;
}

interface TemplateListingItem {
	id: string;
	[key: string]: unknown;
}

interface ContentSectionFormValue {
	name: string;
	title?: string;
	subtitle?: string;
	body?: string;
	useTemplates?: boolean;
	templates?: {
		enabled?: boolean;
		ids?: TemplateListingItem[];
		categories?: string[];
		tags?: string[];
		queryParams?: Record<string, unknown>;
		layout?: string;
		thumbnailStyle?: string;
	};
	visibilityConditions?: unknown[];
	paginator?: {
		pageSize?: number;
		pageSizeOptions?: number[];
	};
	filterByProjectBusiness?: boolean;
	searchbar?: {
		enabled?: boolean;
		label?: string;
		placeholder?: string;
	};
	locale?: SectionLocales;
	sanitizeHtml?: boolean;
}

export interface TransformerContext {
	existingSection?: PublicContentSection;
	usageContext?: string;
}

/**
 * Service responsible for transforming form values into PublicContentSection entities.
 * Extracted from dialog component for better testability and separation of concerns.
 */
@Injectable({
	providedIn: 'root'
})
export class ContentSectionFormTransformerService {
	/**
	 * Transforms form values into a PublicContentSection preview
	 * @param formValue - The current form state
	 * @param context - Additional context like existing section data
	 * @returns PublicContentSection or null if form value is invalid
	 */
	transform(
		formValue: ContentSectionFormValue | undefined,
		context: TransformerContext = {}
	): PublicContentSection | null {
		if (!formValue) return null;

		return {
			// Preserve existing IDs from edit mode
			id: context.existingSection?.id ?? 'preview',

			// Basic text fields with nullish coalescing for safe defaults
			name: formValue.name ?? '',
			title: formValue.title ?? '',
			subtitle: formValue.subtitle ?? '',
			body: formValue.body ?? '',

			// Template configuration
			templates: {
				enabled: formValue.templates?.enabled ?? false,
				ids: formValue.templates?.ids?.length ? formValue.templates?.ids?.map((t: TemplateListingItem) => t.id) : undefined,
				categories: formValue.templates?.categories?.length ? formValue.templates?.categories : undefined,
				tags: formValue.templates?.tags?.length ? formValue.templates?.tags : undefined,
				queryParams: formValue.templates?.queryParams,
				layout: formValue.templates?.layout,
				thumbnailStyle: formValue.templates?.thumbnailStyle ?? 'cover'
			},

			// Searchbar configuration
			searchbar: {
				enabled: formValue.searchbar?.enabled ?? false,
				label: formValue.searchbar?.label ?? '',
				placeholder: formValue.searchbar?.placeholder ?? ''
			},

			// Pagination configuration
			paginator: {
				enabled: true, // Always enabled in preview
				pageSize: formValue.paginator?.pageSize ?? 10,
				pageSizeOptions: formValue.paginator?.pageSizeOptions ?? [5, 10, 20]
			},

			// Feature flags
			useTemplates: formValue.useTemplates ?? false,
			filterByProjectBusiness: formValue.filterByProjectBusiness ?? false,
			visibilityConditions: formValue.visibilityConditions ?? [],

			// Metadata preserved from edit mode
			order: context.existingSection?.order ?? 0,
			locale: formValue.locale ?? {},
			usageContext: context.usageContext ?? 'home',
			created: context.existingSection?.created ?? new Date().toISOString(),
			sanitizeHtml: formValue?.sanitizeHtml
		} as unknown as PublicContentSection;
	}
}
